import { SearchOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { Drawer, Input, Modal, Space, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';

import { Q_CREATE_LIST_TEAM_SALE } from 'apis/GraphQL/Queries/teamSaleQuery';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { XGrayIcon, XIcon } from 'assets/svg';
import { Button, ButtonAction } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import ButtonImport from 'components/Button/ButtonImport';
import TableComponent from 'components/Table/TableComponent';
import { UploadModal } from 'components/UploadModal_V2';
import { useUser } from 'contexts/UserProvider';
import { IParamQraphQL } from 'interfaces/GraphQL/GraphQL';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import {
  IDataCustomerTS,
  IGetAllById,
  STATUS_NAME_ST,
} from 'interfaces/TeamSale/TeamSale';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { toastError, toastSuccess } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { IMPORT_CUSTOMER_INTO_SALES_TEAM } from 'utils/constants/importId';
import ModalAddCustomerToRoute from '../SalePlan/ModalAddCustomerToRoute/ModalAddCustomerToRoute';
import { IOpenDrawer } from './TableSaleTeamPage';

interface IProps {
  open: boolean;
  setOpen: any;
  currentTeam: any;
}

const { PlusOutlined, ExclamationCircleFilled } = icons;

const DrawerCustomerTS = ({ open, setOpen, currentTeam }: IProps) => {
  const { confirm } = Modal;
  const { user } = useUser();
  const [data, setData] = useState<IDataCustomerTS[]>([]),
    [searchValue, setSearchValue] = useState(''),
    [page, setPage] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE }),
    [openAddCustomer, setOpenAddCustomer] = useState(false),
    [isOpenImportModal, setIsOpenImportModal] = useState(false);

  const searchDebounce = useDebounce(searchValue, 500);

  const formatValue = (value: string) => {
    return value === '' || value === null || typeof value === 'undefined'
      ? '-'
      : value;
  };

  const columns: ColumnsTypeCustom = [
    {
      className: 'shadow-right',
      width: 100,
      fixed: 'left',
      align: 'center',
      render: (_: any, record: any) => (
        <Space size={'middle'} className="text-lg ">
          <ButtonAction
            onClick={() => showConfirm(record)}
            tooltip="Xóa"
            disabled={
              user?.isHO || currentTeam?.statusName === STATUS_NAME_ST.DEACTIVE
            }
          >
            {user?.isHO ||
            currentTeam?.statusName === STATUS_NAME_ST.DEACTIVE ? (
              <XGrayIcon />
            ) : (
              <XIcon />
            )}
          </ButtonAction>
        </Space>
      ),
    },
    {
      title: 'STT',
      dataIndex: 'stt',
      width: 50,
      className: 'align-top',
      render: (_: any, __: any, index: number) =>
        index + 1 + page.pageSize * (page.page - 1),
    },
    {
      title: 'Mã khách hàng',
      dataIndex: ['tcustomer', 'sapCustomerId'],
      width: 150,
      className: 'text-start align-top',
      render: (value) => formatValue(value),
    },
    {
      title: 'Mã DMS',
      dataIndex: 'custId',
      width: 150,
      className: 'text-start align-top',
      render: (value) => formatValue(value),
    },
    {
      title: 'Tên cửa hàng',
      dataIndex: ['tcustomer', 'storeName'],
      width: 250,
      className: 'text-start align-top',
      render: (value) => formatValue(value),
    },
    // {
    //   title: 'Vip công',
    //   dataIndex: 'time',
    //   className: 'text-start align-top',
    //   width: 150,
    //   render: (value) => formatValue(value),
    // },
    // {
    //   title: 'Vip món',
    //   dataIndex: 'time',
    //   className: 'text-start align-top',
    //   width: 150,
    //   render: (value) => formatValue(value),
    // },
    {
      title: 'Hạng thẻ',
      className: 'text-start align-top',
      dataIndex: ['tcustomer', 'trank', 'name'],
      width: 100,
      render: (value) => formatValue(value),
    },
    {
      type: 'number',
      currency: 'vi-VN',
      title: 'Doanh thu trung bình',
      dataIndex: ['tcustomer', 'averageRevenue'],
      className: 'text-end align-top',
      width: 200,
      render: (value) => formatValue(value),
    },
    {
      title: 'MST',
      dataIndex: ['tcustomer', 'taxId'],
      className: 'text-start align-top',
      width: 120,
      render: (value) => formatValue(value),
    },
    {
      title: 'SDT',
      dataIndex: ['tcustomer', 'phone'],
      className: 'text-start align-top',
      width: 120,
      render: (value) => formatValue(value),
    },
    {
      title: 'CCCD',
      dataIndex: ['tcustomer', 'cccd'],
      width: 120,
      className: 'text-start align-top',
      render: (value) => formatValue(value),
    },
    {
      title: 'Địa chỉ chi tiết',
      dataIndex: ['tcustomer', 'address'],
      className: 'text-start align-top',
      width: 150,
      render: (value) => formatValue(value),
    },
    {
      title: 'Phường/Xã',
      dataIndex: ['tcustomer', 'tward', 'name'],
      className: 'text-start align-top',
      width: 120,
      render: (value) => formatValue(value),
    },
    {
      title: 'Quận/Huyện',
      dataIndex: ['tcustomer', 'tdistrict', 'name'],
      className: 'text-start align-top',
      width: 120,
      render: (value) => formatValue(value),
    },
    {
      title: 'Tỉnh/Thành phố',
      dataIndex: ['tcustomer', 'tcity', 'name'],
      className: 'text-start align-top',
      width: 150,
      render: (value) => formatValue(value),
    },
  ];

  const getCustomersDetailByIdMutation = useMutation({
      mutationFn: (params: IGetAllById) => SaleTeamApi.searchCustomer(params),
      onSuccess: (res, variables) => {
        if (variables.isExport) {
          columns.shift();
          exportExcel_v2(
            columns,
            res.data.getTeamSalesCust.items,
            `Đội bán hàng - Dánh sách khách hàng trong đội ${currentTeam?.name}`,
          );
        }
        if (res.data.getTeamSalesCust) setData(res.data.getTeamSalesCust.items);
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    { isPending: createCustomersLoading, mutateAsync: createCustomers } =
      useMutation({
        mutationFn: (params: IParamQraphQL) =>
          axiosGraphQL.post('/graphql', params),
        onSuccess: (res) => {
          if (res.data.createManyTeamSalesCust) {
            toastSuccess('Thêm khách hàng thành công');
            getCustomersDetailByIdMutation.mutate({
              teamSalesId: currentTeam?.id,
            });
            // getCustomersByTS({ teamSalesId: currentTeam?.id });
            setOpenAddCustomer(false);
          }
        },
        onError: (error) => {
          console.log(error);
          toastError(`Thêm khách hàng thất bại!. ${error.message}`);
        },
      }),
    { mutate: deleteTeamSales } = useMutation({
      mutationFn: (customerId: string) =>
        SaleTeamApi.deleteTeamSaleCustomer(customerId),
      onSuccess: (res) => {
        if (res.data.deleteTeamSalesCust.code === '200') {
          toastSuccess('Xóa khách hàng thành công');
          getCustomersDetailByIdMutation.mutate({
            teamSalesId: currentTeam?.id,
          });
          setSearchValue('');
          // getCustomersByTS({ teamSalesId: currentTeam?.id });
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const showConfirm = (record: any) => {
    confirm({
      okText: 'Xác nhận',
      cancelText: 'Huỷ',
      title: `Bạn có muốn xoá ${record?.tcustomer?.storeName ?? ''} ra khỏi đội?`,
      icon: <ExclamationCircleFilled />,
      onOk() {
        deleteTeamSales(record.id);
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  useEffect(() => {
    if (!open || !currentTeam) return;

    getCustomersDetailByIdMutation.mutate({
      teamSalesId: currentTeam?.id,
      tcustomer: searchDebounce.trim(),
      ...page,
    });
  }, [open, currentTeam, page, searchDebounce]);

  const onClose = () => {
    setSearchValue('');
    setOpen((prev: IOpenDrawer) => ({ ...prev, isCustomer: !prev.isCustomer }));
  };

  const handleClickCreate = () => {
    setSearchValue('');
    setOpenAddCustomer(true);
  };

  const handleOK = async (data: any[]) => {
    await createCustomers({
      query: Q_CREATE_LIST_TEAM_SALE,
      variables: {
        data: data.map((item: any) => {
          return { custId: item.customerId, teamSalesId: currentTeam.id };
        }),
      },
    });
  };

  // const handleSearch = () => {
  //   const value = searchValue.trim();

  //   if (value === '' || currentTeam === null) return;

  //   getCustomersDetailByIdMutation.mutate({
  //     teamSalesId: currentTeam?.id,
  //     tcustomer: value,
  //   });
  // };

  const handleExportData = () => {
    getCustomersDetailByIdMutation.mutate({
      teamSalesId: currentTeam?.id,
      tcustomer: searchValue.trim(),
      isExport: true,
    });
  };

  const reFetchCustTeamSale = useCallback(() => {
    if (typeof currentTeam === null) return;

    getCustomersDetailByIdMutation.mutate({
      teamSalesId: currentTeam?.id,
      ...page,
    });
  }, [page, currentTeam]);

  return (
    <Drawer
      width={885}
      title={
        <Typography.Text className="text-[#003056] font-bold text-2xl">
          Khách hàng đội quản lý
        </Typography.Text>
      }
      onClose={onClose}
      open={open}
      closable={false}
      destroyOnClose
      extra={<ButtonAction title={<XGrayIcon />} onClick={onClose} />}
    >
      <div className="flex justify-end mb-3 gap-3 items-center">
        <Input
          value={searchValue}
          allowClear
          placeholder="Tìm khách hàng theo tên, mã"
          className="p-2 w-64 bg-[#EFF3F8] rounded-[24px]"
          prefix={<SearchOutlined className="mx-1" />}
          onChange={(e) => setSearchValue(e.target.value)}
        />
        <ButtonExport onClick={handleExportData} />
        {user?.isHO || user?.isAdmin ? (
          <></>
        ) : (
          <ButtonImport
            onClick={() => {
              setIsOpenImportModal(true);
            }}
            disabled={currentTeam?.statusName === STATUS_NAME_ST.DEACTIVE}
          />
        )}
        <UploadModal
          templateFileName="Đội bán hàng - Khách hàng đội quản lý - Template"
          importId={IMPORT_CUSTOMER_INTO_SALES_TEAM}
          open={isOpenImportModal}
          title={'Thêm mới khách hàng trong đội'}
          setOpen={setIsOpenImportModal}
          refetch={reFetchCustTeamSale}
        />
        {user?.isHO ? (
          <></>
        ) : (
          <Button
            title={<PlusOutlined />}
            handleOnclick={handleClickCreate}
            disabled={currentTeam?.statusName === STATUS_NAME_ST.DEACTIVE}
          />
        )}
      </div>
      <TableComponent
        rowKey={(record) => record.id}
        dataSource={data}
        columns={columns}
        loading={
          getCustomersDetailByIdMutation.isPending || createCustomersLoading
        }
        scroll={{ x: 1000 }}
        pagination={{
          current: page.page,
          pageSize: page.pageSize,
          total:
            getCustomersDetailByIdMutation.data?.data.getTeamSalesCust.pageInfo
              ?.totalItems,
          onChange(page, pageSize) {
            setPage({ page, pageSize });
          },
        }}
      />
      <ModalAddCustomerToRoute
        type="TeamSale"
        open={openAddCustomer}
        setOpen={setOpenAddCustomer}
        searchId={currentTeam !== null && currentTeam?.id}
        handleOK={handleOK}
      />
    </Drawer>
  );
};

export default DrawerCustomerTS;
